import React from 'react';
import PlayingCard from './PlayingCard';
import { motion } from 'framer-motion';

/**
 * A single slot on the board.
 *
 * Props:
 *   - card: Card | null
 *   - faceDown: render face-down even if card present (for opponent GK)
 *   - isValid: highlight as a green-glow valid action target
 *   - isSelected: visual selected state (gold)
 *   - isAttackTarget: red glow style
 *   - onClick: callback
 *   - testId: data-testid
 *   - label: optional small label (e.g. "GK")
 *   - size: 'sm' | 'md' (controls width)
 */
export default function BoardSlot({
  card,
  faceDown,
  isValid,
  isSelected,
  isAttackTarget,
  onClick,
  disabled,
  testId,
  label,
  size = 'md',
  cardTestId,
  gkBack,
}) {
  // Phase 5.0B — slot width comes from the single --kk-slot-w CSS var
  // scoped on .kk-gameplay-root. `sm` (compact gameplay) uses the var
  // as-is; the `md` legacy size scales it 1.3× for callers that still
  // pass size='md' (none in current gameplay, but kept for safety).
  const slotW = size === 'sm'
    ? 'var(--kk-slot-w)'
    : 'calc(var(--kk-slot-w) * 1.3)';
  const cls = [
    'kc-slot',
    !card && 'is-empty',
    isValid && 'is-valid',
    isSelected && 'is-selected',
    isAttackTarget && 'is-attacking',
    disabled && 'is-disabled',
  ].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || (!onClick && !isValid)}
      data-testid={testId}
      className={cls}
      style={{
        width: slotW,
        aspectRatio: '2 / 3',
        padding: 'var(--kk-slot-pad)',
        flex: '0 0 auto',
        boxSizing: 'border-box',
      }}
    >
      {card ? (
        <div className="w-full h-full">
          <PlayingCard card={card} faceDown={faceDown} testId={cardTestId} small={size === 'sm'} gkBack={gkBack} />
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-1 opacity-60">
          {label && <span className="line-label">{label}</span>}
        </div>
      )}
    </button>
  );
}
